/**
 * RemindersPage — `/app/reminders`. "Little nudges."
 *
 * On load: GET /me/reminders/upcoming with the saved per-user token. Shows the
 * upcoming nudges as friendly cards (title, detail, "in N days") with Done /
 * Dismiss buttons that call the act endpoint and refresh. If empty, a "Show me
 * my nudges" button POSTs a realistic sample to /me/reminders/report so the
 * screen is alive and demonstrates the value immediately.
 *
 * Honest note: connecting a real calendar / contacts needs a connector and is
 * marked "coming soon". Reporting-your-data (this sample, or a future
 * connector) works today — the nudges you see are computed from exactly that
 * data.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Gift, Phone, Car, Bell, CalendarPlus, Contact } from 'lucide-react';

import {
  getMonitorToken,
  fetchUpcomingReminders,
  reportReminders,
  actOnReminder,
  type NewReminder,
  type UpcomingReminder,
} from '@/pages/monitor/monitorClient';
import { c } from './theme';
import { Card, ComingPill, ErrorCard, EmptyCard, PageHeader } from './ui';

/**
 * A realistic seed so the first open feels alive: a birthday in 4 days, a
 * reconnect after 2 weeks, and a renewal in 10 days — the canonical trio. Due
 * dates are computed from "now" so the "in N days" line always reads right.
 */
function sampleReminders(): NewReminder[] {
  const inDays = (n: number): string => {
    const d = new Date();
    d.setUTCHours(0, 0, 0, 0);
    d.setUTCDate(d.getUTCDate() + n);
    return d.toISOString();
  };
  return [
    {
      kind: 'birthday',
      title: "Dan's birthday",
      detail: 'Want to send a message or grab a gift idea?',
      due_iso: inDays(4),
    },
    {
      kind: 'reconnect',
      title: "You haven't called mom in 2 weeks",
      detail: 'A quick call would make her day.',
      due_iso: inDays(1),
    },
    {
      kind: 'renewal',
      title: 'Car insurance renews in 10 days',
      detail: 'Prices changed this year — want us to compare before it auto-renews?',
      due_iso: inDays(10),
    },
  ];
}

/** Friendly "when" line from a whole-day count. */
function whenLabel(days: number): string {
  if (days < 0) return 'overdue';
  if (days === 0) return 'today';
  if (days === 1) return 'tomorrow';
  return `in ${days} days`;
}

export function RemindersPage() {
  const qc = useQueryClient();
  const token = getMonitorToken();

  const up = useQuery({
    queryKey: ['app', 'reminders', token],
    queryFn: () => fetchUpcomingReminders(token!),
    enabled: Boolean(token),
  });

  const seed = useMutation({
    mutationFn: () => reportReminders(token!, sampleReminders()),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['app', 'reminders', token] }),
  });

  const act = useMutation({
    mutationFn: (v: { id: string; action: 'done' | 'dismiss' }) =>
      actOnReminder(token!, v.id, v.action),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['app', 'reminders', token] }),
  });

  const reminders = up.data ?? [];
  const hasData = reminders.length > 0;

  return (
    <div>
      <PageHeader title="Little nudges" sub="The small stuff that's easy to forget — handled." />

      <div style={{ padding: '14px 18px 8px', display: 'flex', flexDirection: 'column', gap: 12 }}>
        {/* Connect calendar / contacts — honestly coming soon. */}
        <div style={{ display: 'flex', gap: 12 }}>
          <div style={{ flex: 1 }}>
            <Card tone="accent" icon={<CalendarPlus size={22} />} title="Connect calendar" right={<ComingPill />} />
          </div>
          <div style={{ flex: 1 }}>
            <Card tone="accent" icon={<Contact size={22} />} title="Connect contacts" right={<ComingPill />} />
          </div>
        </div>

        <div
          style={{
            fontSize: 12,
            fontWeight: 700,
            color: c.inkSubtle,
            margin: '8px 2px 0',
            textTransform: 'uppercase',
            letterSpacing: '0.06em',
          }}
        >
          Coming up
        </div>

        {up.isPending && <SkeletonList />}

        {up.isError && (
          <ErrorCard onRetry={() => up.refetch()}>We couldn't load your nudges just now.</ErrorCard>
        )}

        {!up.isPending && !up.isError && (
          <>
            {!hasData ? (
              <EmptyCard
                title="Show me my nudges"
                body="Take a quick look and we'll surface the small things coming up — birthdays, people to call, renewals before they sneak up on you."
                actionLabel={seed.isPending ? 'Looking…' : 'Show me my nudges'}
                onAction={() => seed.mutate()}
                disabled={seed.isPending}
              />
            ) : (
              reminders.map((r) => (
                <ReminderCard
                  key={r.id}
                  reminder={r}
                  busy={act.isPending}
                  onAct={(action) => act.mutate({ id: r.id, action })}
                />
              ))
            )}

            {seed.isError && (
              <p style={{ fontSize: 13, color: c.danger, marginTop: 4 }}>
                That didn't go through — tap to try again.
              </p>
            )}
          </>
        )}

        <p style={{ fontSize: 12, color: c.inkSubtle, margin: '12px 2px 0', lineHeight: 1.5 }}>
          Connect your calendar and contacts to get nudges that are actually about your life. Until
          then, these come from the quick look above.
        </p>
      </div>
    </div>
  );
}

// ── Reminder card ─────────────────────────────────────────────────────────────

function reminderIcon(kind: string) {
  if (kind === 'birthday') return Gift;
  if (kind === 'reconnect') return Phone;
  if (kind === 'renewal') return Car;
  return Bell;
}

function ReminderCard({
  reminder,
  busy,
  onAct,
}: {
  reminder: UpcomingReminder;
  busy: boolean;
  onAct: (action: 'done' | 'dismiss') => void;
}) {
  const Icon = reminderIcon(reminder.kind);
  const soon = reminder.days_until <= 3;
  return (
    <div
      style={{
        background: soon ? c.warnSoft : c.surface,
        border: `1px solid ${soon ? c.warnRim : c.rim}`,
        borderRadius: 16,
        padding: '14px 16px',
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
      }}
    >
      <div style={{ display: 'flex', gap: 14, alignItems: 'center' }}>
        <span
          style={{
            flexShrink: 0,
            width: 44,
            height: 44,
            borderRadius: 12,
            background: c.surface,
            border: `1px solid ${c.rim}`,
            color: c.accent,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Icon size={22} />
        </span>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: c.ink }}>{reminder.title}</div>
          {reminder.detail && (
            <div style={{ fontSize: 13, color: c.inkMuted, marginTop: 3, lineHeight: 1.5 }}>
              {reminder.detail}
            </div>
          )}
        </div>
        <span
          style={{
            flexShrink: 0,
            fontSize: 12,
            fontWeight: 800,
            color: soon ? c.warn : c.inkSubtle,
            whiteSpace: 'nowrap',
          }}
        >
          {whenLabel(reminder.days_until)}
        </span>
      </div>

      <div style={{ display: 'flex', gap: 10 }}>
        <ActButton label="Done" primary disabled={busy} onClick={() => onAct('done')} />
        <ActButton label="Dismiss" disabled={busy} onClick={() => onAct('dismiss')} />
      </div>
    </div>
  );
}

function ActButton({
  label,
  primary,
  disabled,
  onClick,
}: {
  label: string;
  primary?: boolean;
  disabled?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      style={{
        flex: 1,
        minHeight: 44,
        fontSize: 14,
        fontWeight: 700,
        color: primary ? '#ffffff' : c.accent,
        background: primary ? c.accent : c.surface,
        border: primary ? 'none' : `1px solid ${c.rim}`,
        borderRadius: 12,
        cursor: disabled ? 'default' : 'pointer',
        opacity: disabled ? 0.6 : 1,
      }}
    >
      {label}
    </button>
  );
}

function SkeletonList() {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {[0, 1, 2].map((i) => (
        <div
          key={i}
          style={{ height: 110, borderRadius: 16, background: c.surface, border: `1px solid ${c.rim}` }}
        />
      ))}
    </div>
  );
}

export default RemindersPage;
